import {
  AutoConcede,
  AutoContinue,
  AutoGimme,
  Clubhouse_LeagueFacilityScope,
  Clubhouse_LeagueHandicapMethod,
  Clubhouse_LeagueScoring,
  Clubhouse_LeagueType,
  Clubhouse_LeagueWizardScheduleMode,
  Clubhouse_LgMatchPlayScoringMode,
  Clubhouse_LgPointsLeagueMode,
  Clubhouse_LgStrokePlayAggregation,
  Clubhouse_LgStrokePlayMaxScorePolicy,
  Clubhouse_LgStrokePlayRanking,
  Clubhouse_LgStrokePlayTieHandling,
  Clubhouse_MatchPlayAssignment,
  Clubhouse_MatchPlayStyle,
  Clubhouse_SideContestType,
  Clubhouse_TeamPlayFormat,
  Clubhouse_TeamPlayStyle,
  GimmeDistance,
  GroundConditions,
  HandicapMode,
  HandicapPercentage,
  PinType,
  ScoringType,
  SkyConditions,
  TeeType,
  TimeOfDay,
  WindDirection,
  WindSpeed,
} from "../enums";
import type {
  Clubhouse_PostLeagueV2,
  Clubhouse_PostLeagueV2Round,
  Clubhouse_RoundSetting,
} from "../types/leagues";

export type Props = Record<string, string | number | boolean>;

type EnumObject = Record<string, string | number>;

/**
 * Names an enum value so Pendo shows "Stroke" rather than 2. Numeric enums are
 * reverse-mapped; string enums already are their name. Unknown values pass
 * through unchanged rather than being dropped.
 */
const label = (e: EnumObject, value: unknown): string | number | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "number") return e[value] ?? value;
  return typeof value === "string" ? value : JSON.stringify(value);
};

const compact = (props: Record<string, unknown>): Props =>
  Object.fromEntries(
    Object.entries(props).filter(
      ([, v]) => v !== undefined && v !== null && v !== "",
    ),
  ) as Props;

const parseHoles = (raw: unknown): number[] | undefined => {
  let value = raw;
  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw);
    } catch {
      return undefined;
    }
  }
  if (!Array.isArray(value)) return undefined;
  const holes = value.filter((h): h is number => Number.isInteger(h));
  return holes.length > 0 ? [...holes].sort((a, b) => a - b) : undefined;
};

/** Collapses sorted holes into ranges: [1..9] → "1-9", [1,2,3,7] → "1-3,7". */
const holeRanges = (holes: number[]): string => {
  const ranges: string[] = [];
  let start = holes[0]!;
  let prev = start;
  for (const h of [...holes.slice(1), Infinity]) {
    if (h === prev + 1) {
      prev = h;
      continue;
    }
    ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
    start = h;
    prev = h;
  }
  return ranges.join(",");
};

const settingProps = (s: Clubhouse_RoundSetting | undefined): Props =>
  compact({
    scoringType: label(ScoringType, s?.scoringType),
    handicapMode: label(HandicapMode, s?.handicapMode),
    handicapPercentage: label(HandicapPercentage, s?.handicapPercentage),
    courseRatingSlope: s?.courseRatingSlope,
    mulligans: s?.mulligans,
    pinType: label(PinType, s?.pinType),
    stimpSetting: s?.stimpSetting,
    mensTee: label(TeeType, s?.mensTee),
    womensTee: label(TeeType, s?.womensTee),
    windSpeed: label(WindSpeed, s?.windSpeed),
    windDirection: label(WindDirection, s?.windDirection),
    groundConditions: label(GroundConditions, s?.groundConditions),
    skyConditions: label(SkyConditions, s?.skyConditions),
    timeConditions: label(TimeOfDay, s?.timeConditions),
    autoConcede: label(AutoConcede, s?.autoConcede),
    autoContinue: label(AutoContinue, s?.autoContinue),
    autoDrop: s?.autoDrop,
    autoGimmes: label(AutoGimme, s?.autoGimmes),
    gimmeDistance: label(GimmeDistance, s?.gimmeDistance),
  });

type RoundLike = {
  courseId?: number | string;
  selectedHolesJson?: string;
  selectedHoles?: string | number[];
  settings?: Clubhouse_RoundSetting;
  roundSettings?: Clubhouse_RoundSetting;
};

/**
 * The settings an operator chose for a round — course, holes, and the round
 * rule set — for "most popular course / holes / scoring" analysis. Works for
 * the create body, the PATCH body, and the season rounds posted with a league.
 */
export function roundSettingProps(round: RoundLike): Props {
  const settings = round.settings ?? round.roundSettings;
  const holes = parseHoles(
    round.selectedHolesJson ?? round.selectedHoles ?? settings?.selectedHoles,
  );
  return compact({
    courseId: round.courseId,
    holeCount: holes?.length,
    holes: holes && holeRanges(holes),
    ...settingProps(settings),
  });
}

/** The settings an operator chose for a league (full create body or a PATCH). */
export function leagueSettingProps(
  body: Partial<Clubhouse_PostLeagueV2>,
): Props {
  const s = body.settings;
  const policy = body.scoringPolicy;
  const rounds: Clubhouse_PostLeagueV2Round[] = body.seasonRounds ?? [];
  const courseIds = [
    ...new Set(
      [body.defaultCourseId, ...rounds.map((r) => r.courseId)].filter(
        (id): id is number => id !== undefined,
      ),
    ),
  ].sort((a, b) => a - b);
  const sideContests = (body.sideContests ?? [])
    .filter((c) => c.isEnabled)
    .map((c) => label(Clubhouse_SideContestType, c.contestType));

  return compact({
    leagueType: label(Clubhouse_LeagueType, s?.leagueType),
    scoringType: label(Clubhouse_LeagueScoring, s?.leagueScoringType),
    teamSize: s?.leagueTeamSize,
    teamPlayFormat: label(Clubhouse_TeamPlayFormat, s?.teamPlayFormat),
    matchPlayStyle: label(Clubhouse_MatchPlayStyle, s?.matchPlayStyle),
    matchPlayAssignment: label(
      Clubhouse_MatchPlayAssignment,
      s?.matchPlayAssignment,
    ),
    matchTeamPlayStyle: label(Clubhouse_TeamPlayStyle, s?.matchTeamPlayStyle),
    defaultGroupSize: s?.defaultGroupSize,
    handicapMethod: label(Clubhouse_LeagueHandicapMethod, s?.handicapMethod),
    handicapMode: label(HandicapMode, s?.handicapMode),
    handicapPercentage: label(HandicapPercentage, s?.handicapPercentage),
    courseRatingSlope: s?.courseRatingSlope,
    mensTee: label(TeeType, s?.mensTee),
    womensTee: label(TeeType, s?.womensTee),
    scheduleMode: label(
      Clubhouse_LeagueWizardScheduleMode,
      body.wizardScheduleMode,
    ),
    facilityScope: label(Clubhouse_LeagueFacilityScope, body.facilityScope),
    allowSubstitutes: body.allowSubstitutes,
    defaultCourseId: body.defaultCourseId,
    courseIds: courseIds.length > 0 ? courseIds.join(",") : undefined,
    handicapEnabled: policy?.handicapEnabled,
    handicapAdjustmentPercent: policy?.handicapAdjustmentPercent,
    strokePlayAggregation: label(
      Clubhouse_LgStrokePlayAggregation,
      policy?.strokePlay?.aggregation,
    ),
    strokePlayRanking: label(
      Clubhouse_LgStrokePlayRanking,
      policy?.strokePlay?.ranking,
    ),
    strokePlayTieHandling: label(
      Clubhouse_LgStrokePlayTieHandling,
      policy?.strokePlay?.tieHandling,
    ),
    strokePlayBestXRounds: policy?.strokePlay?.bestXRoundCount,
    strokePlayMaxScore: label(
      Clubhouse_LgStrokePlayMaxScorePolicy,
      policy?.strokePlay?.maxScorePolicy,
    ),
    matchPlayScoringMode: label(
      Clubhouse_LgMatchPlayScoringMode,
      policy?.matchPlay?.matchScoringMode,
    ),
    pointsLeagueMode: label(
      Clubhouse_LgPointsLeagueMode,
      policy?.pointsLeague?.mode,
    ),
    sideContests: sideContests.length > 0 ? sideContests.join(",") : undefined,
  });
}
